import React, { useCallback } from "react";
import { JointIcon, SlideJointTypeIcon, BallJointTypeIcon } from "../ui/Icons";
import Tooltip from "../ui/Tooltip";
import { getJointTypeMenuOptions } from "../../utils/jointTypeValidation";
import { stripNamespacePrefix as stripPrefix } from "../../utils/nameUtils";

export function useSceneGraphMenuOptions({
  bodies,
  joints,
  getJointReparentTargetDisabledReason,
  handleMoveJointsToBody,
}) {
  const getJointAddOptionsForBody = useCallback(
    (bodyUuid) => {
      const iconByKind = {
        hinge: JointIcon,
        slide: SlideJointTypeIcon,
        ball: BallJointTypeIcon,
      };
      return getJointTypeMenuOptions({
        bodies,
        joints,
        bodyUuid,
        includeFree: false,
      }).map((option) => ({
        ...option,
        Icon: iconByKind[option.kind] || JointIcon,
      }));
    },
    [bodies, joints]
  );

  const getPreferredTendonJointForBody = useCallback(
    (bodyUuid) => {
      const bodyJointUuids = bodyUuid ? bodies?.[bodyUuid]?.joints : null;
      if (Array.isArray(bodyJointUuids)) {
        const bodyJointUuid = bodyJointUuids.find((jointUuid) => {
          const joint = joints?.[jointUuid];
          return joint?.kind === "hinge" || joint?.kind === "slide";
        });
        if (bodyJointUuid) return bodyJointUuid;
      }
      return (
        Object.values(joints || {}).find(
          (joint) => joint?.uuid && (joint.kind === "hinge" || joint.kind === "slide")
        )?.uuid || null
      );
    },
    [bodies, joints]
  );

  const renderJointReparentBodyButton = useCallback(
    (body) => {
      const disabledReason = getJointReparentTargetDisabledReason(body.uuid);
      const button = (
        <button
          key={body.uuid}
          type="button"
          aria-disabled={disabledReason ? "true" : undefined}
          onClick={() => {
            if (disabledReason) return;
            handleMoveJointsToBody(body.uuid);
          }}
          className={
            disabledReason
              ? "w-full text-left rounded-sm px-2 py-1.5 text-sm panel-text-helper opacity-55 cursor-not-allowed"
              : "w-full text-left rounded-sm px-2 py-1.5 text-sm text-gray-200 hover:bg-gray-700/90 transition-colors"
          }
        >
          {stripPrefix(body.name)}
        </button>
      );
      return disabledReason ? (
        <Tooltip key={body.uuid} content={disabledReason}>
          {button}
        </Tooltip>
      ) : (
        button
      );
    },
    [getJointReparentTargetDisabledReason, handleMoveJointsToBody]
  );

  return {
    getJointAddOptionsForBody,
    getPreferredTendonJointForBody,
    renderJointReparentBodyButton,
  };
}
